import { useState } from "react"

import {
  Activity,
  Database,
  Eye,
  Pencil,
  RefreshCw,
  ScanSearch,
  Share2,
  Trash2,
  Users,
} from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { DataConnection, TestDataConnectionResult } from "@/api/data-connections-api"

import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"

import { AdminTableToolbar } from "./admin-table-toolbar"
import { useAdminTableControls } from "../hooks/use-admin-table-controls"

type TestState = {
  status: "idle" | "running" | "success" | "error"
  message?: string
  latencyMs?: number
}

type Props = {
  connections: DataConnection[]
  onToggleStatus: (connection: DataConnection, enabled: boolean) => void
  onEdit: (connection: DataConnection) => void
  onDelete: (connection: DataConnection) => void
  onTest: (connection: DataConnection) => Promise<TestDataConnectionResult>
  onIntrospect?: (connection: DataConnection) => Promise<unknown>
  onShowSchema?: (connection: DataConnection) => void
  onShare?: (connection: DataConnection) => void
  updatingId?: string | null
  disableAll?: boolean
}

export function DataConnectionsTable({
  connections,
  onToggleStatus,
  onEdit,
  onDelete,
  onTest,
  onIntrospect,
  onShowSchema,
  onShare,
  updatingId,
  disableAll,
}: Props) {
  const { t } = useTranslation()
  const [testState, setTestState] = useState<Record<string, TestState>>({})
  const controls = useAdminTableControls(connections, {
    getName: (row) => row.name,
    getSearchText: (row) => [row.name, row.host, row.database].join(" "),
    isEnabled: (row) => row.enabled,
  })

  if (connections.length === 0) {
    return (
      <EmptyState
        icon={Database}
        title={t("adminDashboard.dataConnections.emptyTitle", "No data connections")}
        description={t(
          "adminDashboard.dataConnections.emptyDescription",
          "Add your first Doris connection so the Dash agent can query live data."
        )}
      />
    )
  }

  const runTest = async (conn: DataConnection) => {
    setTestState((prev) => ({ ...prev, [conn.id]: { status: "running" } }))
    try {
      const result = await onTest(conn)
      setTestState((prev) => ({
        ...prev,
        [conn.id]: {
          status: result.success ? "success" : "error",
          message: result.error,
          latencyMs: result.latency_ms,
        },
      }))
    } catch (err) {
      setTestState((prev) => ({
        ...prev,
        [conn.id]: {
          status: "error",
          message: err instanceof Error ? err.message : "Unknown error",
        },
      }))
    }
  }

  return (
    <div className="space-y-3">
      <AdminTableToolbar controls={controls} />
      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
        <div className="col-span-3">{t("adminDashboard.dataConnections.table.name", "Name")}</div>
        <div className="col-span-3">
          {t("adminDashboard.dataConnections.table.endpoint", "Endpoint")}
        </div>
        <div className="col-span-1">
          {t("adminDashboard.dataConnections.table.database", "Database")}
        </div>
        <div className="col-span-2 text-right">
          {t("adminDashboard.dataConnections.table.status", "Status")}
        </div>
        <div className="col-span-3 text-right">
          {t("adminDashboard.dataConnections.table.actions", "Actions")}
        </div>
      </div>

      {controls.rows.map((conn) => {
        const state = testState[conn.id] ?? { status: "idle" as const }
        return (
          <div
            key={conn.id}
            className="admin-responsive-grid-row bg-card grid grid-cols-12 items-center gap-4 rounded-lg border p-4"
          >
            <div className="col-span-3">
              <span className="text-muted-foreground mb-1 block text-xs font-medium lg:hidden">
                Endpoint
              </span>
              <div className="flex items-center gap-2 font-medium">
                <Database className="text-muted-foreground size-4" />
                {conn.name}
              </div>
              <div className="text-muted-foreground font-mono text-xs">{conn.id}</div>
              {(conn.participants?.length ?? 0) > 0 && (
                <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                  <Users className="size-3" />
                  {t("adminDashboard.dataConnections.table.sharedWith", {
                    defaultValue: "Shared with {{count}}",
                    count: conn.participants?.length ?? 0,
                  })}
                </div>
              )}
            </div>

            <div className="col-span-3">
              <div className="font-mono text-sm">
                {conn.host}
                <span className="text-muted-foreground">:{conn.port}</span>
              </div>
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.dataConnections.table.driver", "Driver")}: {conn.driver_type}
                {conn.username &&
                  ` · ${t("adminDashboard.dataConnections.table.user", "user")}: ${conn.username}`}
              </div>
              {state.status !== "idle" && <TestStatus state={state} />}
            </div>

            <div className="col-span-1 flex items-center gap-3 truncate font-mono text-sm lg:block">
              <span className="text-muted-foreground w-24 shrink-0 font-sans text-xs font-medium lg:hidden">
                Database
              </span>
              <span>{conn.database || "—"}</span>
            </div>

            <div className="col-span-2 flex items-center justify-end gap-2">
              <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                Status
              </span>
              <span
                className={cn(
                  "text-xs font-medium",
                  conn.enabled ? "text-success" : "text-muted-foreground"
                )}
              >
                {conn.enabled
                  ? t("adminDashboard.dataConnections.table.statusEnabled", {
                      defaultValue: "Enabled",
                    })
                  : t("adminDashboard.dataConnections.table.statusDisabled", {
                      defaultValue: "Disabled",
                    })}
              </span>
              <Switch
                checked={conn.enabled}
                disabled={disableAll || updatingId === conn.id}
                aria-label={t("adminDashboard.dataConnections.table.toggleAria", {
                  defaultValue: "Toggle {{name}}",
                  name: conn.name,
                })}
                onCheckedChange={(checked) => onToggleStatus(conn, checked)}
              />
            </div>

            <div className="col-span-3 flex flex-wrap items-center justify-end gap-1.5">
              <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                Actions
              </span>
              <Button
                disabled={state.status === "running" || disableAll}
                size="icon"
                title={t("adminDashboard.dataConnections.table.testTooltip", "Test connection")}
                variant="outline"
                aria-label={`${t("adminDashboard.dataConnections.table.testTooltip", "Test connection")} ${conn.name}`}
                onClick={() => void runTest(conn)}
              >
                {state.status === "running" ? (
                  <Spinner className="size-4" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
              </Button>
              {onIntrospect && (
                <Button
                  disabled={disableAll}
                  size="icon"
                  variant="outline"
                  aria-label={`${t("adminDashboard.dataConnections.table.introspectAria", "Scan schema")} ${conn.name}`}
                  title={t(
                    "adminDashboard.dataConnections.table.introspectTooltip",
                    "Scan schema — gives the Dash agent a fresh catalog"
                  )}
                  onClick={() => void onIntrospect(conn)}
                >
                  <ScanSearch className="size-4" />
                </Button>
              )}
              {onShowSchema && (
                <Button
                  disabled={disableAll}
                  size="icon"
                  variant="outline"
                  aria-label={`${t("adminDashboard.dataConnections.table.showSchemaAria", "Show schema catalog")} ${conn.name}`}
                  title={t(
                    "adminDashboard.dataConnections.table.showSchemaTooltip",
                    "Preview the schema catalog the Dash agent reads"
                  )}
                  onClick={() => onShowSchema(conn)}
                >
                  <Eye className="size-4" />
                </Button>
              )}
              {onShare && (
                <Button
                  data-testid={`data-connection-share-${conn.id}`}
                  disabled={disableAll}
                  size="icon"
                  variant="ghost"
                  aria-label={`${t("adminDashboard.dataConnections.table.shareAria", "Share connection")} ${conn.name}`}
                  title={t(
                    "adminDashboard.dataConnections.table.shareTooltip",
                    "Grant users or groups access to this connection"
                  )}
                  onClick={() => onShare(conn)}
                >
                  <Share2 className="size-4" />
                </Button>
              )}
              <Button
                aria-label={`${t("adminDashboard.dataConnections.table.editAria", "Edit")} ${conn.name}`}
                title={t("adminDashboard.dataConnections.table.editAria", "Edit")}
                disabled={disableAll}
                size="icon"
                variant="ghost"
                onClick={() => onEdit(conn)}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                aria-label={`${t("adminDashboard.dataConnections.table.deleteAria", "Delete")} ${conn.name}`}
                title={t("adminDashboard.dataConnections.table.deleteAria", "Delete")}
                disabled={disableAll}
                size="icon"
                variant="ghost"
                onClick={() => onDelete(conn)}
              >
                <Trash2 className="text-destructive size-4" />
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function TestStatus({ state }: { state: TestState }) {
  const { t } = useTranslation()
  if (state.status === "success") {
    return (
      <p className="text-success mt-1 flex items-center gap-1 text-xs">
        <Activity className="size-3" />
        {t("adminDashboard.dataConnections.table.testSuccess", {
          defaultValue: "Connected in {{ms}} ms",
          ms: state.latencyMs ?? 0,
        })}
      </p>
    )
  }
  if (state.status === "error") {
    return (
      <p className="text-destructive mt-1 text-xs">
        {state.message ?? t("adminDashboard.dataConnections.table.testFailed", "Connection failed")}
      </p>
    )
  }
  if (state.status === "running") {
    return (
      <p className="text-muted-foreground mt-1 text-xs">
        {t("adminDashboard.dataConnections.table.testing", "Testing…")}
      </p>
    )
  }
  return null
}
